import { faCalendar, faCircleMinus, faCirclePlus, faClock, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import imageCompression from "browser-image-compression";
import { format, isValid, startOfToday } from "date-fns";
import { cs as dateFnsCs } from "date-fns/locale";
import { useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import { cs as dayPickerCs } from "react-day-picker/locale/cs";
import "react-day-picker/style.css";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { categoryMap } from "../config/CategoryMap";
import { useAlert } from "../context/AlertContext";
import { trackEvent } from "../hooks/useAnalytics";
import { ROUTES } from "../routes/RouteNames";
import "./FormAddJob.css";
import FormImgManager from "./FormImgManager";
import "./GeneralForm.css";
import InputLogin from "./InputLogin";
import LocationAutocomplete from "./LocationAutocomplete";
import TextArea from "./TextArea";

const DURATION_TYPES = [
    { value: "single", label: "Jednorázová akce" },
    { value: "long_term", label: "Dlouhodobá spolupráce" },
];

const PAY_TYPES = [
    { value: "hourly", label: "za hodinu" },
    { value: "daily", label: "za akci" },
];

const LICENSE_OPTIONS = [
    { value: "", label: "Bez požadavku" },
    { value: "B", label: "Skupina B" },
    { value: "C", label: "Skupina C" },
    { value: "D", label: "Skupina D" },
    { value: "E", label: "Skupina E" },
];

const DEFAULT_POSITION = {
    role: "",
    capacity: 1,
    starts_at: "",
    starts_time: "",
    ends_at: "",
    ends_time: "",
    pay_amount: "",
    pay_min: "",
    pay_max: "",
    pay_type: "hourly",
    license_required: "",
};

const getDateValue = (value) =>
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)
        ? value.slice(0, 10)
        : "";

const getTimeValue = (value) =>
    typeof value === "string" && /^\d{2}:\d{2}/.test(value)
        ? value.slice(0, 5)
        : "";

const ROLES_TECH = [
    "light_technician",
    "sound_technician",
    "av_technician",
    "rigger",
    "stagehands",
];

const FormAddJob = ({ initialData, previousJob, isEdit }) => {
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const today = format(new Date(), "yyyy-MM-dd");
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        location_type: "venue",
        location_name: "",
        city: "",
        postcode: "",
        latitude: null,
        longitude: null,
        duration_type: "single",
        starts_at: today,
        starts_time: "",
        ends_at: today,
        ends_time: "",
        schedule_note: "",
        status: "published",
        images: [],
        positions: [{ ...DEFAULT_POSITION, starts_at: today, ends_at: today }],
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [openSelect, setOpenSelect] = useState(null);
    const selectRefs = useRef({});
    const [selectedLocation, setSelectedLocation] = useState(null);

    useEffect(() => {
        if (initialData) {
            setFormData({
                title: initialData.title || "",
                description: initialData.description || "",
                location_type: initialData.location_type || "venue",
                location_name: initialData.location_name || "",
                city: initialData.city || "",
                postcode: initialData.postcode || "",
                latitude: initialData.latitude || null,
                longitude: initialData.longitude || null,
                duration_type: initialData.duration_type || "single",
                starts_at: getDateValue(initialData.starts_at),
                starts_time: initialData.starts_time || "",
                ends_at: getDateValue(initialData.ends_at),
                ends_time: initialData.ends_time || "",
                schedule_note: initialData.schedule_note || "",
                status: initialData.status || "published",
                images: initialData.images || [],
                positions:
                    initialData.positions && initialData.positions.length > 0
                        ? initialData.positions.map((p) => ({
                            role: p.role || "",
                            capacity: p.capacity || 1,
                            starts_at:
                                getDateValue(p.starts_at) ||
                                getDateValue(initialData.starts_at),
                            starts_time:
                                getTimeValue(p.starts_time) ||
                                getTimeValue(p.starts_at) ||
                                initialData.starts_time ||
                                "",
                            ends_at:
                                getDateValue(p.ends_at) ||
                                getDateValue(initialData.ends_at),
                            ends_time:
                                getTimeValue(p.ends_time) ||
                                getTimeValue(p.ends_at) ||
                                initialData.ends_time ||
                                "",
                            pay_amount: p.pay_amount || "",
                            pay_min: p.pay_min || p.pay_amount || "",
                            pay_max: p.pay_max || "",
                            pay_type: p.pay_type || "hourly",
                            license_required: p.license_required || "",
                        }))
                        : [DEFAULT_POSITION],
            });
        }

        if (initialData && initialData.latitude) {
            setSelectedLocation({
                label: initialData.city || "",
                city: initialData.city || "",
                postcode: initialData.postcode || "",
                latitude: initialData.latitude,
                longitude: initialData.longitude,
            });
        }

        trackEvent("page_view", {
            page: isEdit ? "edit_job" : "add_job",
        });
    }, [initialData, isEdit]);

    useEffect(() => {
        if (isEdit || !previousJob) return;

        const previousPositions = previousJob.positions || [];
        const previousPosition =
            previousPositions[previousPositions.length - 1] || {};
        const startsAt =
            getDateValue(previousPosition.starts_at) ||
            getDateValue(previousJob.starts_at) ||
            today;
        const endsAt =
            getDateValue(previousPosition.ends_at) ||
            getDateValue(previousJob.ends_at) ||
            startsAt;
        const startsTime =
            getTimeValue(previousPosition.starts_time) ||
            getTimeValue(previousPosition.starts_at) ||
            getTimeValue(previousJob.starts_time);
        const endsTime =
            getTimeValue(previousPosition.ends_time) ||
            getTimeValue(previousPosition.ends_at) ||
            getTimeValue(previousJob.ends_time);

        setFormData((prev) => ({
            ...prev,
            starts_at: startsAt,
            starts_time: startsTime,
            ends_at: endsAt,
            ends_time: endsTime,
            positions: prev.positions.map((position, index) =>
                index === 0
                    ? {
                          ...position,
                          starts_at: startsAt,
                          starts_time: startsTime,
                          ends_at: endsAt,
                          ends_time: endsTime,
                      }
                    : position,
            ),
        }));
    }, [isEdit, previousJob, today]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openSelect && selectRefs.current[openSelect]) {
                const ref = selectRefs.current[openSelect];
                if (ref.wrapper && !ref.wrapper.contains(event.target)) {
                    setOpenSelect(null);
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openSelect]);

    const handleChange = (field, value) => {
        setFormData((prev) => {
            const newFormData = { ...prev, [field]: value };

            if (field === "starts_at" && prev.duration_type === "single") {
                newFormData.ends_at = value;
            }

            return newFormData;
        });
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const handleLocationChange = (location) => {
        setSelectedLocation(location);
        if (location) {
            setFormData((prev) => ({
                ...prev,
                city: location.city || "",
                postcode: location.postcode || "",
                latitude: location.latitude,
                longitude: location.longitude,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                city: "",
                postcode: "",
                latitude: null,
                longitude: null,
            }));
        }
    };

    const handlePositionChange = (index, field, value) => {
        setFormData((prev) => {
            const newPositions = [...prev.positions];
            newPositions[index] = { ...newPositions[index], [field]: value };
            return { ...prev, positions: newPositions };
        });
    };

    const addPosition = () => {
        setFormData((prev) => {
            const previousPosition = prev.positions[prev.positions.length - 1];
            return {
                ...prev,
                positions: [
                    ...prev.positions,
                    {
                        ...DEFAULT_POSITION,
                        starts_at: previousPosition.starts_at,
                        starts_time: previousPosition.starts_time,
                        ends_at: previousPosition.ends_at,
                        ends_time: previousPosition.ends_time,
                    },
                ],
            };
        });
    };

    const removePosition = (index) => {
        if (formData.positions.length === 1) return;
        const newPositions = [...formData.positions];
        newPositions.splice(index, 1);
        setFormData((prev) => ({ ...prev, positions: newPositions }));
    };

    const toggleSelect = (key) =>
        setOpenSelect(openSelect === key ? null : key);

    const handleSubmit = async (status = "published") => {
        const isSingle = formData.duration_type === "single";
        const hasMissingRequiredField =
            !formData.title.trim() ||
            !formData.description.trim() ||
            !formData.city ||
            formData.positions.some(
                (position) =>
                    !position.role ||
                    !position.capacity ||
                    !position.starts_at ||
                    (isSingle &&
                        (!position.starts_time ||
                            !position.ends_at ||
                            !position.ends_time ||
                            position.pay_amount === "")) ||
                    (!isSingle &&
                        (position.pay_min === "" || position.pay_max === "")),
            );

        if (hasMissingRequiredField) {
            showAlert("error", "Vyplňte všechna povinná pole.");
            return;
        }

        const todayDate = format(startOfToday(), "yyyy-MM-dd");
        if (
            formData.positions.some(
                (position) =>
                    position.starts_at < todayDate ||
                    (isSingle &&
                        position.ends_at &&
                        position.ends_at < todayDate),
            )
        ) {
            showAlert("error", "Datum nemůže být v minulosti.");
            return;
        }

        setErrors({});
        setLoading(true);

        const firstPosition = formData.positions[0];
        const submitData = new FormData();
        submitData.append("title", formData.title);
        submitData.append("description", formData.description);
        submitData.append("location_type", formData.location_type);
        submitData.append("location_name", formData.location_name);
        submitData.append("city", formData.city);
        submitData.append("postcode", formData.postcode);
        submitData.append("latitude", formData.latitude ?? "");
        submitData.append("longitude", formData.longitude ?? "");
        submitData.append("duration_type", formData.duration_type);
        submitData.append("starts_at", firstPosition.starts_at);
        submitData.append("starts_time", firstPosition.starts_time || "");
        submitData.append(
            "ends_at",
            isSingle ? firstPosition.ends_at || "" : "",
        );
        submitData.append(
            "ends_time",
            isSingle ? firstPosition.ends_time || "" : "",
        );
        submitData.append("schedule_note", formData.schedule_note);
        submitData.append("status", status);
        submitData.append("pay_currency", "CZK");

        try {
            const existingUrls = formData.images.filter(
                (img) => typeof img === "string"
            );
            if (isEdit) {
                submitData.append("existing_images", JSON.stringify(existingUrls));
                submitData.append("_method", "PUT");
            }

            const newFiles = formData.images.filter(
                (img) => img instanceof File
            );
            const compressedImages = await Promise.all(
                newFiles.map((img) =>
                    imageCompression(img, {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1920,
                        useWebWorker: true,
                    }),
                ),
            );
            compressedImages.forEach((file) => {
                submitData.append("images[]", file);
            });

            submitData.append(
                "positions",
                JSON.stringify(
                    formData.positions.map((p) => ({
                        role: p.role,
                        capacity: p.capacity,
                        starts_on: p.starts_at,
                        ends_on: isSingle ? p.ends_at || "" : "",
                        starts_at: p.starts_time || "",
                        ends_at: isSingle ? p.ends_time || "" : "",
                        pay_amount: p.pay_amount || p.pay_min || "",
                        pay_max: p.pay_max || null,
                        pay_type: p.pay_type,
                        license_required: p.license_required,
                    })),
                ),
            );

            if (isEdit && initialData?.id) {
                await axiosInstance.post(`/api/jobs/${initialData.id}`, submitData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                showAlert("success", "Pracovní nabídka byla úspěšně aktualizována.");
                trackEvent("job_updated", { job_id: initialData.id });
            } else {
                const response = await axiosInstance.post(
                    "/api/jobs",
                    submitData,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                    },
                );
                showAlert("success", "Pracovní nabídka byla úspěšně přidána.");
                trackEvent("job_created", { job_id: response.data.job?.id });
            }
            navigate(ROUTES.JOBS);
        } catch (error) {
            if (error.response?.status === 401) {
                console.warn("Uživatel není ověřen — bude přesměrován na přihlášení.");
            } else if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                Object.values(error.response.data.errors)
                    .flat()
                    .forEach((message) => showAlert("error", message));
            } else {
                showAlert("error", "Nepodařilo se uložit nabídku. Zkuste to znovu.");
            }
        } finally {
            setLoading(false);
        }
    };

    const getRoleLabel = (value) => {
        return categoryMap[value] || value;
    };

    const getDurationLabel = (value) => {
        return DURATION_TYPES.find((o) => o.value === value)?.label || "-- Vyberte --";
    };

    const TimePickerInline = ({ value: timeValue = "", onTimeChange }) => {
        const HOURS = Array.from({ length: 24 }, (_, i) => i);
        const MINUTES = [0, 10, 20, 30, 40, 50];

        const parseHHMM = (val) => {
            if (!val) return { hours: 0, minutes: 0 };
            const [h, m] = val.split(":");
            return {
                hours: parseInt(h, 10) || 0,
                minutes: parseInt(m, 10) || 0,
            };
        };

        const { hours, minutes } = parseHHMM(timeValue);

        const selectHour = (h) => {
            onTimeChange(h, minutes);
        };

        const selectMinute = (m) => {
            onTimeChange(hours, m);
        };

        return (
            <div className="time-picker">
                <div className="time-picker-column">
                    {HOURS.map((h) => (
                        <button
                            key={h}
                            type="button"
                            className={`time-picker-option ${h === hours ? "selected" : ""}`}
                            onClick={() => selectHour(h)}
                        >
                            {h.toString().padStart(2, "0")}
                        </button>
                    ))}
                </div>
                <span className="time-picker-separator" aria-hidden="true">
                    :
                </span>
                <div className="time-picker-column">
                    {MINUTES.map((m) => (
                        <button
                            key={m}
                            type="button"
                            className={`time-picker-option ${m === minutes ? "selected" : ""}`}
                            onClick={() => selectMinute(m)}
                        >
                            {m.toString().padStart(2, "0")}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    const PickerInput = ({
        type,
        icon: Icon,
        value,
        onChange,
        label,
        id,
        required,
        placeholder = "Vyberte hodnotu",
    }) => {
        const [isOpen, setIsOpen] = useState(false);
        const displayRef = useRef(null);

        useEffect(() => {
            const handleClickOutside = (event) => {
                if (displayRef.current && !displayRef.current.contains(event.target)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () =>
                document.removeEventListener("mousedown", handleClickOutside);
        }, []);

        const isDateType = type === "date" || type === "datetime-local";
        const isTimeType = type === "time" || type === "datetime-local";

        const parseDate = (val) => {
            if (!val) return null;
            let dateStr = val;
            if (type === "datetime-local") {
                dateStr = val.split("T")[0];
            }
            const date = new Date(dateStr);
            return isValid(date) ? date : null;
        };

        const parseTime = (val) => {
            if (!val) return { hours: 0, minutes: 0 };
            let timeStr = val;
            if (type === "datetime-local") {
                timeStr = val.split("T")[1] || "";
            }
            const parts = timeStr.split(":");
            return {
                hours: parseInt(parts[0], 10) || 0,
                minutes: parseInt(parts[1], 10) || 0,
            };
        };

        const selectedDate = parseDate(value);

        const formatDisplay = () => {
            if (!value) return "";
            try {
                if (type === "date") {
                    const date = parseDate(value);
                    return date
                        ? format(date, "d. MMMM yyyy", { locale: dateFnsCs })
                        : "";
                }
                if (type === "time") {
                    return value;
                }
                if (type === "datetime-local") {
                    const dateStr = selectedDate
                        ? format(selectedDate, "d. MMMM yyyy", {
                              locale: dateFnsCs,
                          })
                        : "";
                    const timeStr = value.split("T")[1] || "";
                    return [dateStr, timeStr].filter(Boolean).join(", ");
                }
            } catch {
                return "";
            }
            return "";
        };

        const displayText = formatDisplay() || placeholder;

        const handleDateSelect = (date) => {
            if (!date) return;
            const isoDate = format(date, "yyyy-MM-dd");
            if (type === "date") {
                onChange(isoDate);
                setIsOpen(false);
            } else if (type === "datetime-local") {
                onChange(`${isoDate}T${value?.split("T")[1] || "00:00"}`);
            }
        };

        const handleTimeChange = (hours, minutes) => {
            const timeValue = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
            if (type === "time") {
                onChange(timeValue);
                setIsOpen(false);
            } else if (type === "datetime-local") {
                const datePart = selectedDate
                    ? format(selectedDate, "yyyy-MM-dd")
                    : "";
                onChange(`${datePart}T${timeValue}`);
            }
        };

        return (
            <div className="picker-input-wrapper" ref={displayRef}>
                {label && (
                    <label className="body_base label-move" htmlFor={id}>
                        {label}
                    </label>
                )}
                <div
                    className="picker-input-display"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                >
                    <span
                        className={`picker-input-text ${!value ? "picker-input-placeholder" : ""}`}
                    >
                        {displayText}
                    </span>
                    <FontAwesomeIcon
                        icon={Icon}
                        className="picker-input-icon"
                    />
                    {required && (
                        <input
                            type={type}
                            id={id}
                            value={value || ""}
                            onChange={() => {}}
                            className="picker-input-native"
                            required
                            readOnly
                            tabIndex={-1}
                            aria-hidden="true"
                        />
                    )}
                </div>
                {isOpen && (
                    <div className="picker-popover">
                        {isDateType && (
                            <DayPicker
                                mode="single"
                                selected={selectedDate || undefined}
                                onSelect={handleDateSelect}
                                disabled={{ before: startOfToday() }}
                                locale={dayPickerCs}
                                showOutsideDays
                                fixedWeeks
                                initialFocus={false}
                            />
                        )}
                        {isTimeType && (
                            <TimePickerInline
                                value={
                                    type === "time"
                                        ? value || ""
                                        : value?.split("T")[1] || ""
                                }
                                onTimeChange={handleTimeChange}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    const CustomSelect = ({
        id,
        label,
        value,
        options,
        onChange,
        selectKey,
        isOpen,
        onToggle,
        getLabel,
        extraClasses = "",
    }) => {
        const wrapperRef = useRef(null);

        useEffect(() => {
            selectRefs.current[selectKey] = {
                wrapper: wrapperRef.current,
            };
        }, [selectKey, wrapperRef]);

        const selectedLabel = getLabel(value);

        return (
            <div className="custom-select-wrapper" ref={wrapperRef}>
                <div
                    className={`custom-select-down ${isOpen ? "open" : ""} ${extraClasses}`}
                    onClick={() => onToggle(selectKey)}
                >
                    <span className="selected">
                        {selectedLabel || "-- Vyberte --"}
                    </span>
                    <span className={`arrow ${isOpen ? "rotate" : ""}`}>▼</span>
                </div>
                {isOpen && (
                    <div className="options-down">
                        {options.map((opt) => (
                            <div
                                key={opt.value}
                                className={`option ${value === opt.value ? "selected" : ""}`}
                                onClick={() => {
                                    onChange(opt.value);
                                    onToggle(null);
                                }}
                            >
                                {opt.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <form
            className="form_add_job"
            onSubmit={(e) => {
                e.preventDefault();
                handleSubmit("published");
            }}
        >
            <div className="div1">
                <div className="row_divide_parent">
                    <div className="row_divide_child">
                        <label className="body_base label-move" htmlFor="job-title">Název</label>
                        <InputLogin
                            type="text"
                            name="title"
                            id="job-title"
                            placeholder="např. MightySounds 2027"
                            value={formData.title}
                            onChange={(e) => handleChange("title", e.target.value)}
                            required
                            error_message={errors.title}
                        />
                    </div>
                    <div className="row_divide_child">
                        <label className="body_base label-move" htmlFor="job-type">Spolupráce</label>
                        <CustomSelect
                            id="job-type"
                            value={formData.duration_type}
                            options={DURATION_TYPES}
                            onChange={(value) =>
                                handleChange("duration_type", value)
                            }
                            selectKey="job-type"
                            isOpen={openSelect === "job-type"}
                            onToggle={toggleSelect}
                            getLabel={getDurationLabel}
                        />
                    </div>
                </div>
            </div>

            <div className="div2">
                <label className="body_base label-move" htmlFor="job-description">Popis práce</label>
                <TextArea
                    name="description"
                    id="job-description"
                    placeholder="Podrobný popis pozice, pracovních podmínek, požadavků..."
                    rows={10}
                    maxLength={3000}
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    required
                />
            </div>

            <div className="div3">
                <label className="body_base label-move"> Obrázky (nepovinné) </label>
                <FormImgManager
                    images={formData.images}
                    setImages={(images) => handleChange("images", images)}
                    className="form_images"
                    isLoggedUser={true}
                />
            </div>

            {/* LOKALITA */}
            <div className="div-location">
                <div className="row_divide_parent">
                    <div className="row_divide_child">
                        <label className="body_base label-move" htmlFor="job-location">Oblast </label>
                        <LocationAutocomplete
                            id="job-location"
                            value={selectedLocation}
                            onChange={handleLocationChange}
                            placeholder="Např. Brno, Žižkov, Ostrava"
                            variant="down"
                            withGeolocation={true}
                        />
                    </div>
                    <div className="row_divide_child">
                        <label className="body_base label-move" htmlFor="job-venue">Název místa (nepovinné)</label>
                        <InputLogin
                            type="text"
                            name="location_name"
                            id="job-venue"
                            placeholder="např. Klubovna, Sportovní hala..."
                            value={formData.location_name}
                            onChange={(e) =>
                                handleChange("location_name", e.target.value)
                            }
                        />
                    </div>
                </div>
            </div>

            <div className="div6">
                <h2 className="form-section-title">Pozice</h2>

                {formData.positions.map((position, index) => (
                    <div
                        key={`position-${index}`}
                        className="position-card"
                    >
                        <div className="position-card-header">
                            <h3 className="position-number">
                                Pozice {index + 1}
                            </h3>
                            {formData.positions.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-icon btn-ghost"
                                    onClick={() => removePosition(index)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            )}
                        </div>

                        <div className="row_divide_parent">
                            <div className="row_divide_child">
                                <label className="body_base label-move" htmlFor={`position-role-${index}`}>
                                    Role
                                </label>
                                <CustomSelect
                                    id={`position-role-${index}`}
                                    value={position.role}
                                    options={ROLES_TECH.map((key) => ({
                                        value: key,
                                        label: categoryMap[key],
                                    }))}
                                    onChange={(value) =>
                                        handlePositionChange(index, "role", value)
                                    }
                                    selectKey={`role-${index}`}
                                    isOpen={openSelect === `role-${index}`}
                                    onToggle={toggleSelect}
                                    getLabel={getRoleLabel}
                                />
                            </div>
                            <div className="row_divide_child">
                                <label className="body_base label-move" htmlFor={`position-capacity-${index}`}>
                                    Počet osob
                                </label>
                                <div className="stepper-input-wrapper">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        id={`position-capacity-${index}`}
                                        className="input-login"
                                        value={position.capacity || ""}
                                        required
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (
                                                val === "" ||
                                                (/^[0-9\b]+$/.test(val) && val.length <= 4)
                                            ) {
                                                const numVal =
                                                    val === "" ? "" : parseInt(val);
                                                if (
                                                    numVal === "" ||
                                                    (numVal > 0 && numVal <= 100)
                                                ) {
                                                    handlePositionChange(
                                                        index,
                                                        "capacity",
                                                        numVal,
                                                    );
                                                }
                                            }
                                        }}
                                        onBlur={() => {
                                            if (
                                                !position.capacity ||
                                                position.capacity < 1
                                            ) {
                                                handlePositionChange(
                                                    index,
                                                    "capacity",
                                                    1,
                                                );
                                            }
                                        }}
                                    />
                                    <div className="stepper-input-wrapper-btn-container">
                                        <FontAwesomeIcon
                                            icon={faCircleMinus}
                                            className={`stepper-btn ${(!position.capacity || position.capacity <= 1) ? "button_disabled" : ""}`}
                                            onClick={() =>
                                                handlePositionChange(
                                                    index,
                                                    "capacity",
                                                    Math.max(
                                                        1,
                                                        (parseInt(position.capacity) || 1) - 1,
                                                    ),
                                                )
                                            }
                                        />
                                        <FontAwesomeIcon
                                            icon={faCirclePlus}
                                            className={`stepper-btn ${position.capacity >= 100 ? "button_disabled" : ""}`}
                                            onClick={() => {
                                                const next =
                                                    (parseInt(position.capacity) || 0) + 1;
                                                if (next <= 100)
                                                    handlePositionChange(
                                                        index,
                                                        "capacity",
                                                        next,
                                                    );
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row_divide_child">
                                <label className="body_base label-move" htmlFor={`position-license-${index}`}>
                                    Řidičský průkaz
                                </label>
                                <CustomSelect
                                    id={`position-license-${index}`}
                                    value={position.license_required}
                                    options={LICENSE_OPTIONS}
                                    onChange={(value) =>
                                        handlePositionChange(
                                            index,
                                            "license_required",
                                            value,
                                        )
                                    }
                                    selectKey={`license-${index}`}
                                    isOpen={openSelect === `license-${index}`}
                                    onToggle={toggleSelect}
                                    getLabel={(val) =>
                                        LICENSE_OPTIONS.find(
                                            (o) => o.value === val,
                                        )?.label || "-- Vyberte --"
                                    }
                                />
                            </div>
                        </div>

                        {formData.duration_type === "single" ? (
                            <>
                                <div className="row_divide_parent">
                                    <div className="row_divide_child">
                                        <PickerInput
                                            type="date"
                                            icon={faCalendar}
                                            label="Začátek"
                                            id={`position-starts-${index}-date`}
                                            value={position.starts_at || ""}
                                            onChange={(val) => {
                                                handlePositionChange(
                                                    index,
                                                    "starts_at",
                                                    val,
                                                );
                                                if (
                                                    !position.ends_at ||
                                                    position.ends_at === position.starts_at
                                                ) {
                                                    handlePositionChange(
                                                        index,
                                                        "ends_at",
                                                        val,
                                                    );
                                                }
                                            }}
                                            required
                                        />
                                    </div>
                                    <div className="row_divide_child">
                                        <PickerInput
                                            type="time"
                                            icon={faClock}
                                            label="Začátek (čas)"
                                            id={`position-starts-time-${index}`}
                                            value={position.starts_time || ""}
                                            onChange={(val) =>
                                                handlePositionChange(index, "starts_time", val)
                                            }
                                            required
                                            placeholder="00:00"
                                        />
                                    </div>
                                </div>

                                <div className="row_divide_parent">
                                    <div className="row_divide_child">
                                        <PickerInput
                                            type="date"
                                            icon={faCalendar}
                                            label="Konec (datum)"
                                            id={`position-ends-${index}-date`}
                                            value={position.ends_at || ""}
                                            onChange={(val) => {
                                                handlePositionChange(
                                                    index,
                                                    "ends_at",
                                                    val,
                                                );
                                            }}
                                            required
                                        />
                                    </div>
                                    <div className="row_divide_child">
                                        <PickerInput
                                            type="time"
                                            icon={faClock}
                                            label="Konec (čas)"
                                            id={`position-ends-time-${index}`}
                                            value={position.ends_time || ""}
                                            onChange={(val) =>
                                                handlePositionChange(index, "ends_time", val)
                                            }
                                            required
                                            placeholder="00:00"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="row_divide_parent">
                                <div className="row_divide_child">
                                    <PickerInput
                                        type="date"
                                        icon={faCalendar}
                                        label="Datum nástupu"
                                        id={`position-starts-${index}-date`}
                                        value={position.starts_at || ""}
                                        onChange={(val) =>
                                            handlePositionChange(
                                                index,
                                                "starts_at",
                                                val,
                                            )
                                        }
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {formData.duration_type === "single" ? (
                            <div className="row_divide_parent">
                                <div className="row_divide_child">
                                    <label className="body_base label-move" htmlFor={`position-pay-amount-${index}`}>
                                        Plat ( Kč )
                                    </label>
                                    <InputLogin
                                        type="number"
                                        id={`position-pay-amount-${index}`}
                                        min="0"
                                        step="0.01"
                                        placeholder="0"
                                        value={position.pay_amount}
                                        onChange={(e) =>
                                            handlePositionChange(
                                                index,
                                                "pay_amount",
                                                e.target.value,
                                            )
                                        }
                                        required
                                    />
                                </div>
                                <div className="row_divide_child">
                                    <label className="body_base label-move" htmlFor={`position-pay-type-${index}`}>
                                        Typ výdělku
                                    </label>
                                    <CustomSelect
                                        id={`position-pay-type-${index}`}
                                        value={position.pay_type}
                                        options={PAY_TYPES}
                                        onChange={(value) =>
                                            handlePositionChange(
                                                index,
                                                "pay_type",
                                                value,
                                            )
                                        }
                                        selectKey={`paytype-${index}`}
                                        isOpen={openSelect === `paytype-${index}`}
                                        onToggle={toggleSelect}
                                        getLabel={(val) =>
                                            PAY_TYPES.find(
                                                (o) => o.value === val,
                                            )?.label || "-- Vyberte --"
                                        }
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="row_divide_parent">
                                <div className="row_divide_child">
                                    <label className="body_base label-move" htmlFor={`position-pay-min-${index}`}>
                                        Plat od (Kč)
                                    </label>
                                    <InputLogin
                                        type="number"
                                        id={`position-pay-min-${index}`}
                                        min="0"
                                        step="0.01"
                                        placeholder="0"
                                        value={position.pay_min}
                                        onChange={(e) =>
                                            handlePositionChange(
                                                index,
                                                "pay_min",
                                                e.target.value,
                                            )
                                        }
                                        required
                                    />
                                </div>
                                <div className="row_divide_child">
                                    <label className="body_base label-move" htmlFor={`position-pay-max-${index}`}>
                                        Plat do (Kč)
                                    </label>
                                    <InputLogin
                                        type="number"
                                        id={`position-pay-max-${index}`}
                                        min="0"
                                        step="0.01"
                                        placeholder="0"
                                        value={position.pay_max}
                                        onChange={(e) =>
                                            handlePositionChange(
                                                index,
                                                "pay_max",
                                                e.target.value,
                                            )
                                        }
                                        required
                                    />
                                </div>
                                <div className="row_divide_child">
                                    <label className="body_base label-move" htmlFor={`position-pay-type-${index}`}>
                                        Typ výdělku
                                    </label>
                                    <InputLogin
                                        type="text"
                                        id={`position-pay-type-${index}`}
                                        value="za hodinu"
                                        disabled
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                <div className="div7">
                    <button
                        type="button"
                        className="btn btn-secondary btn-with-icon"
                        onClick={addPosition}
                    >
                        <FontAwesomeIcon icon={faCirclePlus} />
                    </button>
                </div>
            </div>

            <div className="div9">
                <button type="submit" className="form-submit">
                    {loading ? (
                        <div className="spinner spinner-small" />
                    ) : (
                        <p className="strong">{isEdit ? "Uložit změny" : "Přidat práci"}</p>
                    )}
                </button>
            </div>
        </form>
    );
};

export default FormAddJob;
